import React, { useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Checkbox,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Heading,
  Divider,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Textarea,
  Badge,
  useToast
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';

const TestClinicalDocs = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // File state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Validation checkboxes state
  const [validationChecks, setValidationChecks] = useState({
    interviewPerformed: false,
    examPerformed: false,
    airwayManagementRequired: false,
    orientationPerson: false,
    orientationPlace: false,
    orientationTime: false,
    orientationEvent: false,
    teamLeadIndicators: false
  });
  
  // Results state
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [extractedText, setExtractedText] = useState('');

  // File drop handler
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast({
        title: "Invalid file type",
        description: "Please upload only PDF files",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
      setResults(null);
      setError(null);
      setExtractedText('');
    }
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    multiple: false
  });

  // Handle checkbox changes
  const handleCheckboxChange = (field) => {
    setValidationChecks(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    if (!uploadedFile) {
      toast({
        title: "No file selected",
        description: "Please upload a PDF file first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      
      // Add validation checks
      Object.entries(validationChecks).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });

      const response = await fetch('/process-clinical-docs', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
      
      if (data.extractedText) {
        setExtractedText(data.extractedText);
      }

      toast({
        title: "Processing completed",
        description: "Document has been successfully processed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

    } catch (err) {
      setError(err.message);
      toast({
        title: "Processing failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Clear all data
  const handleClear = () => {
    setUploadedFile(null);
    setResults(null);
    setError(null);
    setExtractedText('');
    setValidationChecks({
      interviewPerformed: false,
      examPerformed: false,
      airwayManagementRequired: false,
      orientationPerson: false,
      orientationPlace: false,
      orientationTime: false,
      orientationEvent: false,
      teamLeadIndicators: false
    });
  };

  return (
    <Box maxW="1200px" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        <Heading as="h1" size="xl" textAlign="center" color="blue.600">
          Clinical Document Processing Test Interface
        </Heading>
        
        <Divider />

        {/* File Upload Section */}
        <Card>
          <CardHeader>
            <Heading as="h2" size="md">File Upload (PDF Only)</Heading>
          </CardHeader>
          <CardBody>
            <Box
              {...getRootProps()}
              border="2px dashed"
              borderColor={isDragActive ? "blue.400" : "gray.300"}
              borderRadius="md"
              p={8}
              textAlign="center"
              cursor="pointer"
              bg={isDragActive ? "blue.50" : "gray.50"}
              transition="all 0.2s"
              _hover={{ borderColor: "blue.400", bg: "blue.50" }}
            >
              <input {...getInputProps()} />
              {uploadedFile ? (
                <VStack>
                  <Text color="green.600" fontWeight="semibold">
                    ✓ File uploaded: {uploadedFile.name}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Size: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Text>
                </VStack>
              ) : (
                <VStack>
                  <Text fontSize="lg" color="gray.600">
                    {isDragActive
                      ? "Drop the PDF file here..."
                      : "Drag & drop a PDF file here, or click to select"}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Only PDF files are accepted
                  </Text>
                </VStack>
              )}
            </Box>
          </CardBody>
        </Card>

        {/* Validation Checkboxes Section */}
        <Card>
          <CardHeader>
            <Heading as="h2" size="md">Manual Validation Tests</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <VStack align="start" spacing={3}>
                <Heading as="h3" size="sm" color="gray.700">
                  Core Assessments
                </Heading>
                <Checkbox
                  isChecked={validationChecks.interviewPerformed}
                  onChange={() => handleCheckboxChange('interviewPerformed')}
                >
                  Interview Performed
                </Checkbox>
                <Checkbox
                  isChecked={validationChecks.examPerformed}
                  onChange={() => handleCheckboxChange('examPerformed')}
                >
                  Exam Performed
                </Checkbox>
                <Checkbox
                  isChecked={validationChecks.airwayManagementRequired}
                  onChange={() => handleCheckboxChange('airwayManagementRequired')}
                >
                  Airway Management Required
                </Checkbox>
                <Checkbox
                  isChecked={validationChecks.teamLeadIndicators}
                  onChange={() => handleCheckboxChange('teamLeadIndicators')}
                >
                  Team Lead Indicators
                </Checkbox>
              </VStack>
              
              <VStack align="start" spacing={3}>
                <Heading as="h3" size="sm" color="gray.700">
                  Orientation Checks
                </Heading>
                <Checkbox
                  isChecked={validationChecks.orientationPerson}
                  onChange={() => handleCheckboxChange('orientationPerson')}
                >
                  Person Orientation
                </Checkbox>
                <Checkbox
                  isChecked={validationChecks.orientationPlace}
                  onChange={() => handleCheckboxChange('orientationPlace')}
                >
                  Place Orientation
                </Checkbox>
                <Checkbox
                  isChecked={validationChecks.orientationTime}
                  onChange={() => handleCheckboxChange('orientationTime')}
                >
                  Time Orientation
                </Checkbox>
                <Checkbox
                  isChecked={validationChecks.orientationEvent}
                  onChange={() => handleCheckboxChange('orientationEvent')}
                >
                  Event Orientation
                </Checkbox>
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Action Buttons */}
        <HStack spacing={4} justify="center">
          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Processing..."
            disabled={!uploadedFile}
          >
            Process Document
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClear}
          >
            Clear All
          </Button>
        </HStack>

        {/* Loading Spinner */}
        {isLoading && (
          <Box textAlign="center">
            <Spinner size="xl" color="blue.500" />
            <Text mt={4} color="gray.600">
              Processing clinical document...
            </Text>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Processing Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Results Display */}
        {results && (
          <Card>
            <CardHeader>
              <HStack justify="space-between" align="center">
                <Heading as="h2" size="md">Processing Results</Heading>
                <Badge colorScheme="green" fontSize="md" p={2}>
                  PASS
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack align="start" spacing={4}>
                {/* Patient Count */}
                {results.patientCount !== undefined && (
                  <Box>
                    <Text fontWeight="semibold" color="blue.600">
                      Patient Count: {results.patientCount}
                    </Text>
                  </Box>
                )}

                {/* Field Evaluation Results */}
                {results.fieldEvaluation && (
                  <Box w="100%">
                    <Heading as="h3" size="sm" mb={3} color="gray.700">
                      Field Evaluation Results
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      {Object.entries(results.fieldEvaluation).map(([field, value]) => (
                        <HStack key={field} justify="space-between">
                          <Text>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Text>
                          <Badge
                            colorScheme={value ? "green" : "red"}
                            variant="solid"
                          >
                            {value ? "✓" : "✗"}
                          </Badge>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}

                {/* Additional Results */}
                {results.additionalData && (
                  <Box w="100%">
                    <Heading as="h3" size="sm" mb={3} color="gray.700">
                      Additional Data
                    </Heading>
                    <Text fontSize="sm" color="gray.600" fontFamily="mono">
                      {JSON.stringify(results.additionalData, null, 2)}
                    </Text>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Extracted Text Preview */}
        {extractedText && (
          <Card>
            <CardHeader>
              <Heading as="h2" size="md">Extracted Text Preview</Heading>
            </CardHeader>
            <CardBody>
              <Textarea
                value={extractedText}
                readOnly
                minH="200px"
                maxH="400px"
                fontSize="sm"
                fontFamily="mono"
                bg="gray.50"
                resize="vertical"
              />
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default TestClinicalDocs;